import React, { useMemo } from 'react';
import { Components, Virtuoso, VirtuosoProps } from 'react-virtuoso';
import { styleConstants } from 'theme/styleConstants';
import { styled } from '@mui/material/styles';
import { useAppSelector } from 'store/hooks';
import VirtualListRoot from 'components/participants-list/VirtualListRoot';
import { UserId } from 'features/users/types';
import {
  ContentLibraryUsersVirtualListItemType,
  selectContentLibraryActiveUsers,
} from 'features/content-library/selectors/selectContentLibraryActiveUsers';
import ShareFileUsersListItem from 'features/content-library/components/share-users-selector/ShareFileUsersListItem';
import ShareUsersListItemContent from 'features/content-library/components/share-users-selector/ShareUsersListItemContent';
import { VirtualListItem } from 'components/SectionedVirtualList';

const {
  participantsPanel: { listItemHeight },
} = styleConstants;

export type ToggleUserHandler = (id: UserId) => void;
export type SelectedUserIds = UserId[];

export type ShareUsersListProps = {
  searchQuery: string;
  toggleUser: ToggleUserHandler;
  selectedIds: SelectedUserIds;
  disabled: boolean;
};

export interface ShareUsersListContext {
  toggleUser: ToggleUserHandler;
  selectedIds: SelectedUserIds;
  disabled: boolean;
}

const MUIComponents: Components<ContentLibraryUsersVirtualListItemType, ShareUsersListContext> = {
  List: VirtualListRoot,
  Item: ShareFileUsersListItem,
};

const renderItemContent = (
  index: number,
  item: ContentLibraryUsersVirtualListItemType,
  context: ShareUsersListContext
) => <ShareUsersListItemContent item={item} context={context} />;

const StyledVirtuoso = styled((props: VirtuosoProps<VirtualListItem, ShareUsersListContext>) => (
  <Virtuoso {...props} style={{ height: listItemHeight * 4 }} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '4px',
}));

const ShareFileUsersList = React.memo(
  ({ searchQuery, toggleUser, selectedIds, disabled }: ShareUsersListProps) => {
    const users = useAppSelector((state) => selectContentLibraryActiveUsers(state, searchQuery));

    const context: ShareUsersListContext = useMemo(
      () => ({
        toggleUser,
        selectedIds,
        disabled,
      }),
      [toggleUser, selectedIds, disabled]
    );

    return (
      <StyledVirtuoso
        data={users}
        components={MUIComponents}
        itemContent={renderItemContent}
        context={context}
        fixedItemHeight={listItemHeight}
      />
    );
  }
);

export default ShareFileUsersList;
